<form [formGroup]="form" class="request-form">
  <!-- Requested-count fulfilment: how the assigned resources cover each seniority -->
  @if (requestedResources.length > 0) {
    <div class="fulfillment-summary">
      <div class="fulfillment-summary-header">
        <span class="fulfillment-summary-title">
          Requested Resources
          <mat-icon
            class="label-help-icon"
            matTooltip="How the seniority of the resources you assign covers the requested counts. Resources matching a specific seniority fill that row first; any extras count toward an &quot;Any&quot; row."
            matTooltipPosition="above"
          >
            help
          </mat-icon>
        </span>
        <span
          class="fulfillment-summary-total"
          [class.met]="fulfilledTotal >= requestedTotal"
        >
          {{ fulfilledTotal }} of {{ requestedTotal }} filled
        </span>
      </div>

      <div class="fulfillment-chips">
        @for (row of fulfillmentBreakdown; track row.seniorityCode) {
          <div
            class="fulfillment-chip"
            [class.met]="row.fulfilled >= row.requested"
          >
            <span class="fulfillment-chip-label">{{ row.label }}</span>
            <span class="fulfillment-chip-count">
              {{ row.fulfilled }}/{{ row.requested }}
            </span>
            @if (row.fulfilled >= row.requested) {
              <mat-icon class="fulfillment-chip-icon">check_circle</mat-icon>
            }
          </div>
        }
      </div>

      @if (extraAssignedCount > 0) {
        <div class="fulfillment-extra">
          {{ extraAssignedCount }} assigned beyond the requested count.
        </div>
      }
    </div>
  }

  <!-- Per-matter resource assignments -->
  <div class="form-section">
    <label class="field-label">
      {{ assignmentsLabel }}
      <mat-icon
        class="label-help-icon"
        matTooltip="Expand a matter to assign resources to it. You can also leave this empty and assign resources later."
        matTooltipPosition="above"
      >
        help
      </mat-icon>
    </label>

    @if (availableMatters.length === 0) {
      <div class="matter-assignments-empty">
        Select one or more matters in the Client &amp; Timing step to assign
        resources.
      </div>
    } @else {
      <div class="matter-assignments-list">
        @for (m of availableMatters; track m.clientMatterNumber) {
          <div
            class="matter-assignment-group"
            [class.open]="isMatterExpanded(m.clientMatterNumber)"
          >
            <div class="matter-assignment-header">
              <button
                type="button"
                class="matter-assignment-toggle"
                (click)="toggleMatter(m.clientMatterNumber)"
              >
              <mat-icon class="expand-icon">
                {{
                  isMatterExpanded(m.clientMatterNumber)
                    ? "expand_less"
                    : "expand_more"
                }}
              </mat-icon>
              <div class="matter-assignment-title">
                <span class="matter-name">{{
                  m.matterName || m.clientMatterNumber
                }}</span>
                <span class="matter-number">{{ m.clientMatterNumber }}</span>
              </div>
              @if (matterRequestedSummary(m.clientMatterNumber)) {
                <span class="matter-requested-summary">
                  Requested: {{ matterRequestedSummary(m.clientMatterNumber) }}
                </span>
              }
              @if (assignmentsForMatter(m.clientMatterNumber).length > 0) {
                <span class="matter-resource-count">
                  {{ assignmentsForMatter(m.clientMatterNumber).length }}
                  assigned
                </span>
              }
              </button>
            </div>

            @if (isMatterExpanded(m.clientMatterNumber)) {
              <div class="matter-assignment-body">
                <div class="matter-assignment-columns">
                  <div class="matter-assignment-search-col">
                    <div class="preferred-resources-container">
                  <div class="search-input-wrapper">
                    <mat-form-field appearance="outline" class="search-field">
                      <mat-icon matPrefix>search</mat-icon>
                      <input
                        matInput
                        placeholder="Search resources to assign..."
                        autocomplete="off"
                        [value]="matterSearchText.get(m.clientMatterNumber) || ''"
                        (focus)="onMatterResourceFocus(m.clientMatterNumber)"
                        (input)="
                          onMatterResourceInput(m.clientMatterNumber, $event)
                        "
                        (blur)="onMatterResourceBlur(m.clientMatterNumber)"
                      />
                      @if (matterResourceLoading.has(m.clientMatterNumber)) {
                        <mat-spinner
                          matSuffix
                          diameter="16"
                          class="field-spinner"
                        ></mat-spinner>
                      }
                    </mat-form-field>

                    @if (
                      (matterResourceSuggestions.get(m.clientMatterNumber) || [])
                        .length > 0
                    ) {
                      <div class="search-suggestions">
                        @for (
                          r of matterResourceSuggestions.get(
                            m.clientMatterNumber
                          ) || [];
                          track r.employeeId
                        ) {
                          <div
                            class="suggestion-item"
                            (click)="
                              addResourceToMatter(m.clientMatterNumber, r)
                            "
                          >
                            <div class="resource-info">
                              <span class="resource-name">
                                {{ r.fullName }}
                                @if (r.isPreferred) {
                                  <span class="preferred-tag">(preferred)</span>
                                }
                              </span>
                              <span class="resource-role">{{
                                r.secondary
                              }}</span>
                            </div>
                          </div>
                        }
                      </div>
                    } @else if (
                      matterResourceNotFound.has(m.clientMatterNumber) &&
                      (matterSearchText.get(m.clientMatterNumber) || "").trim()
                        .length >= 3
                    ) {
                      <div class="search-suggestions">
                        <div class="suggestion-item no-results">
                          <div class="resource-info">
                            <span class="no-results-text"
                              >No search results</span
                            >
                          </div>
                        </div>
                      </div>
                    }
                  </div>

                  @if (assignmentsForMatter(m.clientMatterNumber).length > 0) {
                    <div class="added-resources-list">
                      @for (
                        a of assignmentsForMatter(m.clientMatterNumber);
                        track a.employeeId
                      ) {
                        <div class="assignment-item">
                          <div class="resource-info">
                            <span class="resource-name">
                              {{ a.name }}
                              @if (isPreferred(a.employeeId)) {
                                <span class="preferred-tag">(preferred)</span>
                              }
                            </span>
                            <span class="resource-role">
                              {{ a.role }}
                              @if (a.seniority) {
                                <span class="resource-seniority"
                                  >· {{ a.seniority }}</span
                                >
                              }
                            </span>
                          </div>
                          <mat-form-field
                            appearance="outline"
                            class="assignment-type-field"
                          >
                            <mat-select
                              [value]="a.assignmentTypeCode"
                              (selectionChange)="
                                changeAssignmentType(a.employeeId, $event.value)
                              "
                            >
                              @for (
                                opt of assignmentTypeOptions;
                                track opt.code
                              ) {
                                <mat-option [value]="opt.code">{{
                                  opt.name
                                }}</mat-option>
                              }
                            </mat-select>
                          </mat-form-field>
                          <button
                            mat-icon-button
                            type="button"
                            class="remove-button"
                            (click)="
                              removeResourceFromMatter(
                                m.clientMatterNumber,
                                a.employeeId
                              )
                            "
                          >
                            <mat-icon class="remove-icon">remove_circle</mat-icon>
                          </button>
                        </div>
                      }
                    </div>
                  } @else {
                    <div class="matter-no-assignments">
                      No resources assigned to this matter yet.
                    </div>
                  }
                    </div>
                  </div>

                  <div class="matter-requested-col">
                    <label class="field-label matter-subsection-label">
                      Requested for this Matter
                      <span class="resource-count"
                        >({{ matterRequestedTotal(m.clientMatterNumber) }}
                        total)</span
                      >
                      <mat-icon
                        class="label-help-icon"
                        matTooltip="How many resources, by seniority, you need for this specific matter. These counts are separate from the request-level Requested Resources."
                        matTooltipPosition="above"
                      >
                        help
                      </mat-icon>
                    </label>

                    <app-requested-resources-editor
                      [rows]="requestedForMatter(m.clientMatterNumber)"
                      (rowsChange)="
                        onMatterRequestedRowsChange(m.clientMatterNumber, $event)
                      "
                    ></app-requested-resources-editor>
                  </div>
                </div>
              </div>
            }
          </div>
        }
      </div>
    }
  </div>
</form>
